/**
 * Atualiza <title>, meta description, Open Graph e canonical no cliente (SPA).
 * Busca indexação ainda depende de conteúdo + tempo; isso melhora snippet e compartilhamento.
 */

const DEFAULT_TITLE = 'PowerAgents | Plataforma de Agentes'
const DEFAULT_DESCRIPTION =
  'Orquestre agentes de IA ligados a APIs, bancos, e-mail e WhatsApp. Rede de coordenadores e executores, chat e painel de execuções — ambientes isolados por cliente.'
const OG_DESCRIPTION_FALLBACK =
  'PowerAgents: agentes de IA integrados aos seus sistemas, com rede inteligente, chat e visibilidade de cada execução.'

const ABOUT_TITLE = 'Quem somos | PowerAgents'
const ABOUT_DESCRIPTION =
  'Conheca o PowerAgents: plataforma para empresas criarem e operarem agentes de IA por ambiente.'

/** Títulos da área logada (evita indexar painel; foco em uso interno) */
const LOGGED_IN_ROUTE_TITLE = {
  dashboard: 'Painel',
  profile: 'Perfil',
  'tenant-settings': 'Ambiente',
  users: 'Usuários',
  'agents-coordinators': 'Coordenadores',
  'agents-executors': 'Executores',
  'agents-network': 'Redes de Agentes',
  'agents-schedules': 'Agendamentos',
  'coordinator-groups': 'Grupos de Coordenadores',
  'agent-dashboard': 'Execuções',
  'agent-run-logs': 'Registo de logs',
  'api-connectors': 'APIs externas',
  'database-connectors': 'Banco de Dados',
  'email-delivery-connectors': 'E-mail de envio',
  'email-inbox-connectors': 'Caixas de e-mail (IMAP)',
  'whatsapp-delivery-connectors': 'WhatsApp (envio)',
  'coordinator-chat': 'Chat com coordenadores',
  about: 'Quem somos'
}

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLinkRel(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function removeMetaByName(name) {
  document.head.querySelectorAll(`meta[name="${name}"]`).forEach((el) => el.remove())
}

export function absolutePageUrl(pathname = window.location.pathname) {
  const origin = window.location.origin
  const path = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname
  if (!path || path === '') return `${origin}/`
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function truncateText(str, max) {
  if (!str) return ''
  const t = str.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1).trim()}…`
}

function defaultOgImageUrl() {
  return `${window.location.origin}/poweragent-logo.png`
}

/**
 * Páginas públicas (home, about, tenant na raiz)
 */
export function applyDefaultPageMeta() {
  removeMetaByName('robots')

  document.title = DEFAULT_TITLE
  upsertMeta('name', 'description', DEFAULT_DESCRIPTION)

  upsertMeta('property', 'og:title', DEFAULT_TITLE)
  upsertMeta('property', 'og:description', OG_DESCRIPTION_FALLBACK)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'PowerAgent')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', DEFAULT_TITLE)
  upsertMeta('name', 'twitter:description', truncateText(OG_DESCRIPTION_FALLBACK, 200))

  upsertLinkRel('canonical', url)
}

export function applyAboutPageMeta() {
  removeMetaByName('robots')

  document.title = ABOUT_TITLE
  upsertMeta('name', 'description', ABOUT_DESCRIPTION)

  upsertMeta('property', 'og:title', ABOUT_TITLE)
  upsertMeta('property', 'og:description', truncateText(ABOUT_DESCRIPTION, 200))
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'PowerAgent')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', ABOUT_TITLE)
  upsertMeta('name', 'twitter:description', truncateText(ABOUT_DESCRIPTION, 200))

  upsertLinkRel('canonical', url)
}

export function applyAdminPageMeta() {
  const title = 'Administracao | PowerAgent'
  document.title = title
  upsertMeta('name', 'robots', 'noindex, nofollow')
  upsertMeta('name', 'description', 'Area administrativa PowerAgent.')

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', 'Area administrativa PowerAgent.')
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'PowerAgent')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertLinkRel('canonical', url)
}

export function applyLoggedInPageMeta(routeKey) {
  const label = LOGGED_IN_ROUTE_TITLE[routeKey] || 'Painel'
  const title = `${label} | PowerAgent`
  document.title = title

  upsertMeta('name', 'robots', 'noindex, nofollow')
  upsertMeta('name', 'description', `Area logada PowerAgent - ${label}.`)

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', `Área logada — ${label}.`)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'PowerAgent')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertLinkRel('canonical', url)
}
